/* eslint-disable */
import { Button, Icon, Modal, Popover, SpaceBetween, Spinner, StatusIndicator } from '@amzn/awsui-components-react';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { CHALLENGE_DETAILS_ROUTES } from '../../../../routes';
import { useApi } from '../../../../store/api.context';
import { useChallenges } from '../../../../store/challenge.context';
import { COMMENT_MAX_LENGTH, COMMENT_MIN_LENGTH, useCreateChallenge } from '../../../../store/create-challenge.context';
import { useUser } from '../../../../store/user.context';
import { ChallengeStatus, ChallengeUtils, ChallengeWarningResponse } from '../../../../types/Challenge';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { preProdLogger } from '../../../../utils/log.utils';
import { ConfirmModal } from '../../../common/ConfirmModal';
import { ReviewButtonType } from '../../ChallengeModel';

const ReviewButtonPanel = () => {
  const {
    currentReview,
    submitReview,
    readyForReview,
    beginReview,
    cancelReview,
    reviewMode,
    approveChallenge,
    updatePublicStatus,
    challenge,
    getReview,
    setCurrentReview,
    reviewLoading,
    setReviewMode,
  } = useChallenges();

  const { t } = useTranslation();
  const { challengeId }: { challengeId: string } = useParams();
  const { user } = useUser();
  const { challengesApi } = useApi();
  const { editMode } = useCreateChallenge();
  const history = useHistory();

  useEffect(() => {
    if (challenge?.status === ChallengeStatus.IN_REVIEW && ChallengeUtils.hasReviewer(challenge, user)) {
      // check for existing reviews with current user as reviewer (only when challenge is in review)
      // other wise user cannot submit their reviews since the status will be out of date
      getReview(true);
    }

    return () => {
      setCurrentReview(undefined);
      setReviewMode(false);
    };
  }, []);

  const [confirmReadyForReviewVisible, setConfirmReadyForReviewVisible] = useState(false);
  const [confirmApprovalVisible, setConfirmApprovalVisible] = useState(false);
  const [readyForReviewComment, setReadyReadyForReviewComment] = useState('');

  const [approvalWarning, setApprovalWarning] = useState<ChallengeWarningResponse | undefined>();
  const [isFetchingApprovalWarnings, setIsFetchingApprovalWarnings] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState<React.JSX.Element | undefined>();
  const [approvalComment, setApprovalComment] = useState('');
  const [commentErrorText, setCommentErrorText] = useState('');
  const commentConstraintText = t(i18nKeys.challenges.challengeDetails.popover.commentConstraint, {
    minLength: COMMENT_MIN_LENGTH,
    maxLength: COMMENT_MAX_LENGTH,
  });

  const handleApproval = () => {
    if (
      !approvalComment ||
      approvalComment.length < COMMENT_MIN_LENGTH ||
      approvalComment.length > COMMENT_MAX_LENGTH
    ) {
      setCommentErrorText(t(i18nKeys.challenges.challengeDetails.popover.commentInvalidError));
      return;
    }
    if (approvalWarning) {
      approveChallenge(approvalWarning, approvalComment);
    }
    setConfirmApprovalVisible(false);
  };

  const handleSubmitForReview = () => {
    if (
      !readyForReviewComment ||
      readyForReviewComment.length < COMMENT_MIN_LENGTH ||
      readyForReviewComment.length > COMMENT_MAX_LENGTH
    ) {
      setCommentErrorText(t(i18nKeys.challenges.challengeDetails.popover.commentInvalidError));
      return;
    }
    readyForReview(readyForReviewComment);
    setConfirmReadyForReviewVisible(false);
  };

  const getApprovalMessage = async () => {
    if (challenge) {
      // get a list of warnings associated with approving this challenge
      // i.e. "approving this challenge will redeploy X active lab accounts"
      setIsFetchingApprovalWarnings(true);
      const aw: ChallengeWarningResponse = await challengesApi
        .getApprovalWarnings(challenge, false)
        .finally(() => setIsFetchingApprovalWarnings(false));

      setApprovalWarning(aw);

      if (aw && aw.warnings !== undefined && aw.warnings.length > 0) {
        const modalMessages: React.JSX.Element[] = [];
        modalMessages.push(
          <div key="approval-warning-header">
            <Icon variant="warning" name="status-warning" />{' '}
            {t(i18nKeys.challenges.challengeDetails.messages.activeEventsUsingChallenge)}
          </div>
        );

        for (const warning of aw.warnings) {
          modalMessages.push(<div key={`approval-warning-${modalMessages.length}`}>{warning}</div>);
        }

        modalMessages.push(<br key={`approval-warning-line-break-0`} />);
        modalMessages.push(
          <div key={`approval-warning-${modalMessages.length}`}>
            {t(i18nKeys.challenges.challengeDetails.messages.approval)}
          </div>
        );

        setApprovalMessage(<>{...modalMessages}</>);
      } else {
        setApprovalMessage(<div>{t(i18nKeys.challenges.challengeDetails.messages.approval)}</div>);
      }
    }
  };

  const shouldShowButton = (buttonType: ReviewButtonType): boolean => {
    if (editMode) {
      return false;
    }

    if (user && challenge) {
      switch (buttonType) {
        case ReviewButtonType.REQUEST_REVIEW:
          return (
            !challenge?.isArchived &&
            challenge?.status === ChallengeStatus.DRAFT &&
            ChallengeUtils.isCollaborator(challenge, user)
          );
        case ReviewButtonType.START_REVIEW: {
          return (
            currentReview == null &&
            (ChallengeUtils.needsReviewer(challenge, user) || ChallengeUtils.needsReview(challenge, user))
          );
        }
        case ReviewButtonType.COMPLETE_REVIEW: {
          return (
            currentReview != null &&
            ChallengeUtils.isBeingReviewed(challenge, user) &&
            ChallengeUtils.isComplete(currentReview)
          );
        }
        case ReviewButtonType.CANCEL_REVIEW:
          return currentReview != null && ChallengeUtils.isBeingReviewed(challenge, user);
        case ReviewButtonType.APPROVE_REVIEW:
          return (
            challenge?.status === ChallengeStatus.IN_REVIEW && // challenge must be in review
            (user?.isChallengeAdmin || user?.isSuperAdmin || ChallengeUtils.isBarRaiser(challenge, user)) // user is either challenge admin OR bar raiser or jam-admin
            // && challenge?.barRaiserReviewStatus === ChallengeReviewStatus.APPROVED
          );
        case ReviewButtonType.REVIEW_MODE:
          return currentReview != null && !reviewMode && ChallengeUtils.isBeingReviewed(challenge, user);
        default:
          return false;
      }
    }

    return false;
  };

  const isMissingLcsrTicket = useMemo(() => {
    if (!challenge?.createdDate || challenge?.createdDate < new Date('2025-04-01T00:00:00.000Z').getTime()) {
      return false;
    }

    // The ticket URL is purged from scrubbed responses; hasLcsrTicket still signals its presence.
    return !(challenge?.props.lcsrTicket || challenge?.props.hasLcsrTicket);
  }, [challenge]);

  return (
    <SpaceBetween size="s" direction="horizontal" alignItems="center">
      {shouldShowButton(ReviewButtonType.REQUEST_REVIEW) && (
        <Button
          id="challenge-review-button-submit"
          data-testid="challenge-review-button-submit"
          onClick={() => setConfirmReadyForReviewVisible(true)}
        >
          {t(i18nKeys.challenges.challengeDetails.buttons.submitForReview)}
        </Button>
      )}

      {shouldShowButton(ReviewButtonType.START_REVIEW) && (
        <Button
          id="challenge-review-button-begin-review"
          data-testid="challenge-review-button-begin-review"
          onClick={beginReview}
          loading={reviewLoading}
        >
          {t(i18nKeys.challenges.challengeDetails.buttons.beginReview)}
        </Button>
      )}

      {shouldShowButton(ReviewButtonType.CANCEL_REVIEW) && (
        <Button
          id="challenge-review-button-unassign-reviewer"
          data-testid="challenge-review-button-unassign-reviewer"
          onClick={cancelReview}
          loading={reviewLoading}
        >
          {t(i18nKeys.challenges.challengeDetails.buttons.unassignReviewer)}
        </Button>
      )}

      {shouldShowButton(ReviewButtonType.REVIEW_MODE) && (
        <Button
          id="challenge-review-button-start"
          data-testid="challenge-review-button-start"
          onClick={() => setReviewMode(true)}
        >
          {t(i18nKeys.challenges.challengeDetails.buttons.startReviewMode)}
        </Button>
      )}

      {shouldShowButton(ReviewButtonType.COMPLETE_REVIEW) && (
        <Button
          id="challenge-review-button-complete"
          data-testid="challenge-review-button-complete"
          variant="primary"
          onClick={() => {
            submitReview()
              .then(() => {
                const token = challenge?.challengeId;
                if (token != null && token !== '') {
                  history.push(CHALLENGE_DETAILS_ROUTES.Revisions.resolve(token));
                }
              })
              .catch((err) => {
                preProdLogger('Error submitting review', err);
              });
          }}
          loading={reviewLoading}
        >
          {t(i18nKeys.challenges.challengeDetails.buttons.completeChallengeReview)}
        </Button>
      )}

      {challenge?.status !== ChallengeStatus.APPROVED &&
        (shouldShowButton(ReviewButtonType.APPROVE_REVIEW) || user?.isChallengeAdmin || user?.isSuperAdmin) && (
          <>
            {isMissingLcsrTicket && (
              <StatusIndicator type="warning">
                <Popover content={t(i18nKeys.challenges.challengeDetails.messages.approveDisabledNoLcsrWarning)}>
                  {t(i18nKeys.challenges.challengeDetails.messages.approveDisabledNoLcsr)}
                </Popover>
              </StatusIndicator>
            )}

            <Button
              id="challenge-review-button-approve"
              data-testid="challenge-review-button-approve"
              variant={'primary'}
              disabled={!!challenge?.isArchived || (!user?.isSuperAdmin && isMissingLcsrTicket)}
              disabledReason={
                challenge?.isArchived
                  ? t(i18nKeys.challenges.challengeDetails.messages.disabledIsArchived)
                  : isMissingLcsrTicket
                  ? t(i18nKeys.challenges.challengeDetails.messages.approveDisabledNoLcsr)
                  : undefined
              }
              onClick={() => {
                getApprovalMessage().then(() => setConfirmApprovalVisible(true));
              }}
            >
              {t(i18nKeys.challenges.challengeDetails.buttons.approve)}
            </Button>
          </>
        )}
      <Modal
        visible={isFetchingApprovalWarnings}
        onDismiss={() => setIsFetchingApprovalWarnings(false)}
        header={t(i18nKeys.challenges.challengeDetails.headings.checkingActiveEventsUsingChallenge)}
      >
        <Spinner /> {t(i18nKeys.challenges.challengeDetails.messages.checkingActiveEventsUsingChallenge)}
      </Modal>
      <ConfirmModal
        message={t(i18nKeys.challenges.challengeDetails.messages.readyForReview)}
        visible={confirmReadyForReviewVisible}
        confirmBtnLabel={t(i18nKeys.general.submit)}
        textInput={readyForReviewComment}
        onTextInput={setReadyReadyForReviewComment}
        onConfirm={handleSubmitForReview}
        onCancel={() => setConfirmReadyForReviewVisible(false)}
        constraintText={commentConstraintText}
        errorText={commentErrorText}
        data-testid="submit-for-review-modal"
      />
      <ConfirmModal
        title={t(i18nKeys.challenges.challengeDetails.titles.approval)}
        message={approvalMessage}
        visible={confirmApprovalVisible}
        textInput={approvalComment}
        onTextInput={setApprovalComment}
        confirmBtnLabel={t(i18nKeys.challenges.challengeDetails.yesLabels.approval)}
        cancelBtnLabel={t(i18nKeys.challenges.challengeDetails.cancelLabels.approval)}
        onConfirm={handleApproval}
        onCancel={() => setConfirmApprovalVisible(false)}
        constraintText={commentConstraintText}
        errorText={commentErrorText}
        data-testid="approve-review-modal"
      />
    </SpaceBetween>
  );
};

export default ReviewButtonPanel;
